import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Collapse from '@mui/material/Collapse';
import debounce from 'lodash/debounce';
import { styled, useTheme } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

import { Chart, DatePicker, SearchBar, SmallInfoCard, Switch } from '@bloomays-lib/ui.shared';
import {
  Maybe,
  TalentBoardJobFunnelPhase,
  ApolloGqlTalentBoardStats,
  StatusType,
  ApolloGqlJobFunnel,
  TalentBoardEventType,
} from '@bloomays-lib/types.shared';
import { getPhaseColor } from '../../helpers/utils';
import JobStatusCard from '../molecules/JobStatusCard';
import useTalentBoardFiltersStore from '../../stores/talentBoardFiltersStore';
import { Logger } from '../../services/service-logger';

const logger = Logger('TalentBoardHeader');

export type TalentBoardSectionProps = {
  job: ApolloGqlJobFunnel;
  talentsData: {
    notValidatedTalentsLength: number;
    qualificationTalentsLength: number;
    positionnementTalentsLength: number;
    itwClientTalentsLength: number;
  };
  stats: ApolloGqlTalentBoardStats | null;
  loading: boolean;
};

const getImage = (loading: boolean, jobStatus: Maybe<StatusType>, totalPositionnementTalents: number) => {
  if (loading) return 'loading';
  if (jobStatus === StatusType.win) return 'trophy';
  if (totalPositionnementTalents === 0) return 'loupe';
  if (totalPositionnementTalents > 0) return 'cible';
  return 'loupe';
};

const getWelcomeMessage = (
  t: (key: string, options?: unknown) => string,
  loading: boolean,
  jobStatus: Maybe<StatusType>,
  totalPositionnementTalents: number,
) => {
  if (loading) return '...';
  if (jobStatus === StatusType.win) return t('welcomeMessageWin');
  if (totalPositionnementTalents === 0) return t('welcomeMessageZero');
  return t('welcomeMessage', { count: totalPositionnementTalents });
};

const TalentBoardHeader = ({ talentsData, stats, loading, job }: TalentBoardSectionProps) => {
  const { t } = useTranslation(['talentBoard', 'talentTool']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const {
    topProfileFilter,
    setTopProfileFilter,
    notMarkedAsSeenFilter,
    setNotMarkedAsSeenFilter,
    searchQuery,
    setSearchQuery,
    addedAfterDate,
    setAddedAfterDate,
    resetFilters,
  } = useTalentBoardFiltersStore();

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value: string, job: ApolloGqlJobFunnel) => {
        logger.info('Traffic', {
          page: 'TalentBoard',
          data: { searchQuery: value },
          evt: { name: TalentBoardEventType.TalentboardFilterTalentBySearchQuery },
          job: {
            id: job.id,
            name: job.name,
          },
        });
        setSearchQuery(value);
      }, 500),
    [setSearchQuery],
  );

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery]);

  const chartData: Record<string, { color: string; number: number }> = {
    [t(`jobFunnelPhases.${TalentBoardJobFunnelPhase.NotValidated}`, { ns: 'talentBoard' })]: {
      color: getPhaseColor(TalentBoardJobFunnelPhase.NotValidated),
      number: talentsData.notValidatedTalentsLength,
    },
    [t(`jobFunnelPhases.${TalentBoardJobFunnelPhase.Qualification}`, { ns: 'talentBoard' })]: {
      color: getPhaseColor(TalentBoardJobFunnelPhase.Qualification),
      number: talentsData.qualificationTalentsLength,
    },
    [t(`jobFunnelPhases.${TalentBoardJobFunnelPhase.Positionnement}`, { ns: 'talentBoard' })]: {
      color: getPhaseColor(TalentBoardJobFunnelPhase.Positionnement),
      number: talentsData.positionnementTalentsLength,
    },
    [t(`jobFunnelPhases.${TalentBoardJobFunnelPhase.ITWClient}`, { ns: 'talentBoard' })]: {
      color: getPhaseColor(TalentBoardJobFunnelPhase.ITWClient),
      number: talentsData.itwClientTalentsLength,
    },
  };

  const welcomeMessage = getWelcomeMessage(t, loading, job.status, talentsData.positionnementTalentsLength);
  const welcomeImage = getImage(loading, job.status, talentsData.positionnementTalentsLength);
  const contactedTalents = loading ? '...' : stats?.contacted.toString() || '0';

  const haveFilters = topProfileFilter || notMarkedAsSeenFilter || Boolean(searchQuery) || Boolean(addedAfterDate);

  return (
    <Container>
      <Grid container spacing={1} alignItems="stretch" direction={{ md: 'column', lg: 'row' }}>
        <Grid size={{ xs: 12, md: 12, lg: 5, xl: 5 }}>
          <JobStatusCard
            title={t('welcome', { ns: 'talentBoard' })}
            description={welcomeMessage}
            imgName={welcomeImage}
            loading={loading}
          />
        </Grid>

        <Grid container size={{ xs: 12, md: 12, lg: 3, xl: 2 }} sx={{ display: 'flex' }}>
          <Box sx={{ flex: 1, display: 'flex' }}>
            <SmallInfoCard
              iconName="people"
              title={t('kpis.contactedTalents', { ns: 'talentBoard' })}
              value={contactedTalents}
              loading={loading}
            />
          </Box>
        </Grid>

        {!isMobile && (
          <Grid container size={{ xs: 6, md: 12, lg: 4, xl: 5 }} sx={{ display: 'flex' }}>
            <Box sx={{ flex: 1, display: 'flex' }}>
              <Chart
                title={t('kpis.processProgress', { ns: 'talentBoard' })}
                unit={t('kpis.profils', { ns: 'talentBoard' })}
                data={chartData}
                loading={loading}
              />
            </Box>
          </Grid>
        )}
      </Grid>
      {isMobile && (
        <FilterToggleButton onClick={() => setFiltersExpanded(!filtersExpanded)}>
          <FilterListIcon sx={{ fontSize: '20px' }} />
          <span>{t('filters', { ns: 'talentTool' })}</span>
          {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </FilterToggleButton>
      )}
      <Collapse in={isMobile ? filtersExpanded : true}>
        <Grid container spacing={1} direction={{ xs: 'column', sm: 'column', md: 'row', lg: 'row', xl: 'row' }}>
          <Grid
            container
            size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
            direction={{ md: 'row', lg: 'row', xl: 'row' }}
          >
            <Grid size={{ xs: 7, sm: 8, md: 7, lg: 7, xl: 7 }}>
              <SearchBar
                placeholder={t('search', { ns: 'talentBoard' })}
                searchValue={localSearchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setLocalSearchQuery(value);
                  debouncedSetSearchQuery(value, job);
                }}
                variant="talentBoard"
              />
            </Grid>
            <Grid size={{ xs: 5, sm: 4, md: 5, lg: 5, xl: 5 }}>
              <DatePicker
                openTo="day"
                label={t('addedAfter', { ns: 'talentBoard' })}
                value={addedAfterDate}
                handleChange={(date) => {
                  logger.info('Traffic', {
                    page: 'TalentBoard',
                    data: { addedAfterDate: date },
                    evt: { name: TalentBoardEventType.TalentboardFilterByDate },
                    job: {
                      id: job.id,
                      name: job.name,
                    },
                  });
                  setAddedAfterDate(date);
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={{ xs: 14, sm: 1, md: 10, lg: 0, xl: 0 }}
            size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
            direction={{ md: 'row', lg: 'row', xl: 'row' }}
          >
            <Grid size={{ xs: 4, sm: 3, md: 4, lg: 4, xl: 4 }}>
              <Switch
                label={t('topProfils', { ns: 'talentBoard' })}
                checked={topProfileFilter}
                onSwitch={(_, checked) => {
                  logger.info('Traffic', {
                    page: 'TalentBoard',
                    data: { topProfileFilter: checked },
                    evt: { name: TalentBoardEventType.TalentboardFilterByTopProfile },
                    job: {
                      id: job.id,
                      name: job.name,
                    },
                  });
                  setTopProfileFilter(checked);
                }}
                sx={{
                  textWrap: 'nowrap',
                }}
              />
            </Grid>
            <Grid size={{ xs: 4, sm: 3, md: 4, lg: 4, xl: 4 }}>
              <Switch
                label={t('notSeen', { ns: 'talentBoard' })}
                checked={notMarkedAsSeenFilter}
                onSwitch={(_, checked) => {
                  logger.info('Traffic', {
                    page: 'TalentBoard',
                    data: { notMarkedAsSeenFilter: checked },
                    evt: { name: TalentBoardEventType.TalentboardFilterByNotMarkedAsSeen },
                    job: {
                      id: job.id,
                      name: job.name,
                    },
                  });
                  setNotMarkedAsSeenFilter(checked);
                }}
                sx={{
                  textWrap: 'nowrap',
                }}
              />
            </Grid>
            {haveFilters && (
              <Grid
                size={{ xs: 4, sm: 1, md: 4, lg: 1, xl: 1 }}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                <FilterAltOffIcon
                  sx={{ fontSize: '20px', marginLeft: '8px', cursor: 'pointer' }}
                  onClick={() => {
                    logger.info('Traffic', {
                      page: 'TalentBoard',
                      evt: { name: TalentBoardEventType.TalentboardResetFilters },
                      job: {
                        id: job.id,
                        name: job.name,
                      },
                    });
                    resetFilters();
                  }}
                  titleAccess={t('resetFilters', { ns: 'talentTool' }) || ''}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Collapse>
    </Container>
  );
};

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: '8px',
  },
}));

const FilterToggleButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  backgroundColor: '#fbeef2',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  width: '100%',
  justifyContent: 'space-between',
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.neutral[20],
  },
  '&:active': {
    backgroundColor: theme.palette.neutral[30],
  },
}));

export default TalentBoardHeader;
